import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, PageHeader } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, LogOut, ArrowLeft, Database, TicketPercent } from "lucide-react";

function PromoSection() {
  const [promos, setPromos] = useState(null);
  const [pform, setPform] = useState({ code: "", percentOff: "", duration: "once", durationInMonths: "3", maxRedemptions: "" });
  const [pbusy, setPbusy] = useState(false);

  const loadPromos = () => api.get("/admin/promos").then((r) => setPromos(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { loadPromos(); }, []);

  const createPromo = async () => {
    setPbusy(true);
    try {
      await api.post("/admin/promos", {
        code: pform.code, percentOff: parseFloat(pform.percentOff), duration: pform.duration,
        durationInMonths: pform.duration === "repeating" ? parseInt(pform.durationInMonths, 10) : undefined,
        maxRedemptions: pform.maxRedemptions ? parseInt(pform.maxRedemptions, 10) : undefined,
      });
      toast.success(`Promo ${pform.code.toUpperCase()} created`);
      setPform({ code: "", percentOff: "", duration: "once", durationInMonths: "3", maxRedemptions: "" });
      loadPromos();
    } catch (e) { toast.error(errMsg(e)); }
    setPbusy(false);
  };

  const toggle = async (p) => {
    try {
      await api.patch(`/admin/promos/${p.id}`, { active: !p.active });
      toast.success(`${p.code} ${p.active ? "deactivated" : "reactivated"}`);
      loadPromos();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const durLabel = (p) => p.duration === "repeating" ? `${p.durationInMonths} months` : p.duration === "forever" ? "forever" : "first payment";

  return (
    <div className="mt-10" data-testid="promo-admin-section">
      <div className="flex items-center gap-2 mb-4">
        <TicketPercent className="h-5 w-5 text-[#5B93F0]" />
        <h2 className="font-display font-black text-2xl tracking-tighter">Promo Codes</h2>
        <span className="text-xs text-neutral-500">Created in Stripe automatically — usable at any checkout</span>
      </div>

      <div className="border border-neutral-800 bg-neutral-900/40 p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-neutral-400">Code</Label>
            <Input data-testid="promo-code-input" className="w-36 bg-neutral-900 border-neutral-700" placeholder="SPRING25"
              value={pform.code} onChange={(e) => setPform({ ...pform, code: e.target.value.toUpperCase() })} /></div>
          <div><Label className="text-neutral-400">% off</Label>
            <Input data-testid="promo-percent-input" type="number" min="1" max="100" className="w-24 bg-neutral-900 border-neutral-700"
              value={pform.percentOff} onChange={(e) => setPform({ ...pform, percentOff: e.target.value })} /></div>
          <div><Label className="text-neutral-400">Duration</Label>
            <Select value={pform.duration} onValueChange={(v) => setPform({ ...pform, duration: v })}>
              <SelectTrigger data-testid="promo-duration-select" className="w-40 bg-neutral-900 border-neutral-700"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="once">First payment only</SelectItem>
                <SelectItem value="repeating">Repeating (months)</SelectItem>
                <SelectItem value="forever">Forever</SelectItem>
              </SelectContent>
            </Select></div>
          {pform.duration === "repeating" && (
            <div><Label className="text-neutral-400"># months</Label>
              <Input data-testid="promo-months-input" type="number" min="1" max="36" className="w-24 bg-neutral-900 border-neutral-700"
                value={pform.durationInMonths} onChange={(e) => setPform({ ...pform, durationInMonths: e.target.value })} /></div>
          )}
          <div><Label className="text-neutral-400">Max uses (optional)</Label>
            <Input data-testid="promo-max-input" type="number" min="1" className="w-32 bg-neutral-900 border-neutral-700" placeholder="unlimited"
              value={pform.maxRedemptions} onChange={(e) => setPform({ ...pform, maxRedemptions: e.target.value })} /></div>
          <Button data-testid="promo-create-btn" onClick={createPromo} className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]"
            disabled={pbusy || !pform.code.trim() || !pform.percentOff}>
            {pbusy ? "Creating…" : "Create promo"}
          </Button>
        </div>
      </div>

      <div className="border border-neutral-800 overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 bg-neutral-900">
              <th className="px-4 py-2">Code</th><th className="px-4 py-2">Discount</th><th className="px-4 py-2">Duration</th>
              <th className="px-4 py-2">Redeemed</th><th className="px-4 py-2">Status</th><th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {promos === null ? (
              <tr><td colSpan={6} className="px-4 py-6 text-neutral-500">Loading promo codes…</td></tr>
            ) : promos.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-neutral-500">No promo codes yet.</td></tr>
            ) : promos.map((p) => (
              <tr key={p.id} data-testid="promo-row" className="border-t border-neutral-800 hover:bg-neutral-900/50">
                <td className="px-4 py-2.5 font-mono2 font-bold">{p.code}</td>
                <td className="px-4 py-2.5">{p.percentOff ? `${p.percentOff}% off` : p.amountOff ? `$${p.amountOff} off` : "—"}</td>
                <td className="px-4 py-2.5 text-neutral-400">{durLabel(p)}</td>
                <td className="px-4 py-2.5">{p.timesRedeemed}{p.maxRedemptions ? ` / ${p.maxRedemptions}` : ""}</td>
                <td className="px-4 py-2.5"><StatusBadge value={p.active ? "Active" : "Inactive"} /></td>
                <td className="px-4 py-2.5 text-right">
                  <button data-testid="promo-toggle-btn" onClick={() => toggle(p)} className={`text-xs underline ${p.active ? "text-red-400 hover:text-red-300" : "text-neutral-400 hover:text-white"}`}>
                    {p.active ? "Deactivate" : "Reactivate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminTenants() {
  const { user, logout } = useAuth();
  const [tenants, setTenants] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", adminName: "", adminEmail: "", adminPassword: "", seedDemo: true });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/tenants").then((r) => setTenants(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.adminEmail) { delete payload.adminEmail; delete payload.adminPassword; delete payload.adminName; }
      await api.post("/admin/tenants", payload);
      toast.success("Tenant environment provisioned");
      setOpen(false);
      setForm({ name: "", adminName: "", adminEmail: "", adminPassword: "", seedDemo: true });
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const toggleStatus = async (t) => {
    try {
      await api.patch(`/admin/tenants/${t.id}`, { status: t.status === "Active" ? "Suspended" : "Active" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <header className="border-b border-neutral-800">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/projects" className="text-neutral-400 hover:text-white"><ArrowLeft className="h-4 w-4" /></Link>
            <div className="font-display font-black tracking-tighter text-lg">Ganttastic.app <span className="text-[#5B93F0]">PLATFORM ADMIN</span></div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span>{user?.name}</span>
            <button data-testid="logout-btn" onClick={logout} className="p-1.5 hover:text-red-400"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline-label text-neutral-500">Elevated access</div>
            <h1 className="font-display font-black text-3xl tracking-tighter">Tenant Environments</h1>
          </div>
          <Button data-testid="new-tenant-btn" onClick={() => setOpen(true)} className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]">
            <Plus className="h-4 w-4 mr-1" />Provision Tenant
          </Button>
        </div>

        <div className="border border-neutral-800 overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 bg-neutral-900">
                <th className="px-4 py-2">Tenant</th><th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Users</th><th className="px-4 py-2">Projects</th>
                <th className="px-4 py-2">Created</th><th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {tenants === null ? (
                <tr><td colSpan={6} className="px-4 py-6 text-neutral-500">Loading…</td></tr>
              ) : tenants.map((t) => (
                <tr key={t.id} data-testid="tenant-row" className="border-t border-neutral-800 hover:bg-neutral-900/50">
                  <td className="px-4 py-2.5 font-medium">{t.name}<div className="text-[10px] text-neutral-600 font-mono2">{t.id}</div></td>
                  <td className="px-4 py-2.5"><StatusBadge value={t.status} /></td>
                  <td className="px-4 py-2.5">{t.userCount}</td>
                  <td className="px-4 py-2.5">{t.projectCount}</td>
                  <td className="px-4 py-2.5 text-neutral-400">{(t.createdAt || "").slice(0, 10)}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button data-testid="toggle-tenant-status-btn" onClick={() => toggleStatus(t)} className="text-xs underline text-neutral-400 hover:text-white mr-3">
                      {t.status === "Active" ? "Suspend" : "Activate"}
                    </button>
                    <button data-testid="seed-demo-btn" onClick={async () => { await api.post(`/admin/tenants/${t.id}/seed-demo`); toast.success("Demo project seeded"); load(); }}
                      className="text-xs underline text-neutral-400 hover:text-white inline-flex items-center gap-1">
                      <Database className="h-3 w-3" />Seed demo
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <PromoSection />
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Provision New Tenant Environment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Tenant / organization name</Label><Input data-testid="tenant-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Admin name</Label><Input data-testid="tenant-admin-name-input" value={form.adminName} onChange={(e) => setForm({ ...form, adminName: e.target.value })} /></div>
              <div><Label>Admin email</Label><Input data-testid="tenant-admin-email-input" type="email" value={form.adminEmail} onChange={(e) => setForm({ ...form, adminEmail: e.target.value })} /></div>
            </div>
            <div><Label>Admin password</Label><Input data-testid="tenant-admin-password-input" type="password" value={form.adminPassword} onChange={(e) => setForm({ ...form, adminPassword: e.target.value })} /></div>
            <div className="flex items-center justify-between border border-border p-3">
              <div>
                <div className="text-sm font-medium">Seed demo project</div>
                <div className="text-xs text-muted-foreground">Load the Northern Health QC4 Health hybrid project</div>
              </div>
              <Switch data-testid="seed-demo-switch" checked={form.seedDemo} onCheckedChange={(v) => setForm({ ...form, seedDemo: v })} />
            </div>
            <Button data-testid="provision-tenant-submit-btn" onClick={create} disabled={busy || !form.name} className="w-full rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]">
              {busy ? "Provisioning…" : "Provision environment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
